import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from './useAuth';
import { deviceModelsByKey } from '../utils/modelLists';
import { isUsableProviderConfig } from '../utils/userPageHelpers';

const DEFAULT_AGENT_ASSIGNMENT = {
  provider: 'stackform',
  model_name: 'default-rapid-model',
  persona_id: '',
};

const EMPTY_AGENT_ASSIGNMENTS = {
  capo: { ...DEFAULT_AGENT_ASSIGNMENT },
  cardian: { ...DEFAULT_AGENT_ASSIGNMENT },
  aeri: { ...DEFAULT_AGENT_ASSIGNMENT },
};

const API_URL = process.env.REACT_APP_AUTH_BACKEND_URL;

// POST a JSON body to a profile endpoint and return the parsed response.
// Throws on a non-ok response so callers can surface the message.
const postProfile = async (path, body) => {
  const res = await fetch(`${API_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body || {}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.status !== 'ok') {
    throw new Error(data.detail || `Request failed: ${path}`);
  }
  return data;
};

export const useModelSettings = () => {
  const { user } = useAuth();
  const [provider, setProvider] = useState('default');
  const [model, setModel] = useState('default-rapid-model');
  const [agentModelAssignments, setAgentModelAssignments] = useState({ ...EMPTY_AGENT_ASSIGNMENTS });
  const [providerConfigs, setProviderConfigs] = useState({});
  const [personas, setPersonas] = useState([]);
  const [toolApiKeys, setToolApiKeys] = useState({});
  const [requireProviderKeys, setRequireProviderKeys] = useState(false);
  const [modelRecommended, setModelRecommended] = useState({});
  // The platform catalog by provider; null until it loads.
  const [modelCatalog, setModelCatalog] = useState(null);
  // The user's custom models by provider family (/api/user/api_keys); null
  // until the keys load.
  const [availableModels, setAvailableModels] = useState(null);
  // Each local key's installed device models, with the provider configs
  // they were read for: they've loaded once read for the current ones.
  const [deviceModels, setDeviceModels] = useState({
    configs: null,
    byKey: {},
  });
  // True while /api/user/api_keys is being fetched.
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [activeProfileName, setActiveProfileName] = useState('Default');
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  // Snapshot of the model selection as last loaded for the active profile;
  // lets callers detect unsaved edits before switching profiles.
  const [modelBaseline, setModelBaseline] = useState(null);
  // Monotonic id so a slow, superseded fetch never clobbers a newer one
  // when the user switches profiles rapidly.
  const fetchSeqRef = useRef(0);

  const refreshModelSettings = useCallback(() => {
    setRefreshTrigger(prev => prev + 1);
  }, []);

  // ── Model profiles ──────────────────────────────────────────────────
  // The active profile is the source of truth for which models a run uses.
  // The left panel only switches the active profile; creating / duplicating
  // / renaming / deleting profiles lives solely in the Model Defaults tab
  // (UserPage owns those calls). Switching then refreshes so the in-memory
  // settings above reflect the newly active profile.
  const switchProfile = useCallback(async (name) => {
    await postProfile('/api/user/active-profile', { profile_name: name });
    setActiveProfileName(name);
    refreshModelSettings();
  }, [refreshModelSettings]);

  useEffect(() => {
    if (!user?.id) return;
    // Tag this fetch; a later switch bumps the seq so this one's result is
    // dropped if it resolves out of order (#14).
    const seq = (fetchSeqRef.current += 1);
    const fetchSettings = async () => {
      setIsRefreshing(true);
      try {
        const response = await fetch(
          `${API_URL}/api/user/api_keys?user_id=${user.id}`,
          { credentials: 'include' }
        );
        const data = await response.json();
        if (seq !== fetchSeqRef.current) return; // superseded — ignore

        if (response.ok && data.status === 'ok') {
          const nextProvider =
            data.orchestrator_model?.provider || 'default';
          const nextModel =
            data.orchestrator_model?.model || 'default-rapid-model';
          setProvider(nextProvider);
          setModel(nextModel);

          if (data.provider_configs) {
            setProviderConfigs(data.provider_configs);
          }
          setAvailableModels(data.available_models || {});

          let nextAssignments;
          if (
            data.agent_model_assignments &&
            Object.keys(data.agent_model_assignments).length > 0
          ) {
            const normalizedDbAssignments = Object.fromEntries(
              Object.entries(data.agent_model_assignments).map(
                ([name, assignment]) => [
                  name,
                  {
                    provider: assignment?.provider || '',
                    model_name: assignment?.model_name || '',
                    persona_id: assignment?.persona_id || '',
                  },
                ]
              )
            );
            nextAssignments = {
              ...EMPTY_AGENT_ASSIGNMENTS,
              ...normalizedDbAssignments,
            };
          } else {
            nextAssignments = { ...EMPTY_AGENT_ASSIGNMENTS };
          }
          setAgentModelAssignments(nextAssignments);

          if (data.personas) {
            setPersonas(data.personas.filter(p => p.id && p.title));
          }

          if (Array.isArray(data.tool_api_keys)) {
            const keyMap = Object.fromEntries(
              data.tool_api_keys.map(({ tool_name, has_key }) => [
                tool_name,
                !!has_key,
              ])
            );
            setToolApiKeys(keyMap);
          }

          setRequireProviderKeys(!!data.require_provider_keys);

          if (data.active_profile_name) {
            setActiveProfileName(data.active_profile_name);
          }
          if (Array.isArray(data.profiles)) {
            setProfiles(data.profiles);
          }

          // Record the just-loaded selection as the clean baseline.
          setModelBaseline({
            provider: nextProvider,
            model: nextModel,
            agentModelAssignments: nextAssignments,
          });
        }
      } catch (error) {
        console.error('Failed to fetch model settings:', error);
      } finally {
        // A superseded fetch leaves the flag to the newer one.
        if (seq === fetchSeqRef.current) setIsRefreshing(false);
      }
    };

    fetchSettings();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, refreshTrigger]);

  // The provider/model catalog is global (not profile-scoped), so fetch it
  // once per user instead of on every profile switch (#17).
  useEffect(() => {
    if (!user?.id) return;
    const fetchCatalog = async () => {
      try {
        const catalogRes = await fetch(
          `${API_URL}/api/provider_models`,
          { credentials: 'include' }
        );
        const catalogData = await catalogRes.json();
        if (catalogRes.ok && catalogData.status === 'ok') {
          setModelRecommended(catalogData.recommended || {});
          setModelCatalog(catalogData.catalog || {});
        }
      } catch (err) {
        console.error('Failed to fetch model catalog:', err);
      }
    };
    fetchCatalog();
  }, [user?.id]);

  // A local key's models come from the user's Connect devices.  Only users
  // with a local key ask for them, once the keys have loaded: until then no
  // key is known to be local, and a local field reads "Loading models…".
  const keysLoaded = availableModels !== null;
  useEffect(() => {
    if (!user?.id || !keysLoaded) return undefined;
    const hasLocal = (providerConfigs.local || []).some((cfg) =>
      isUsableProviderConfig('local', cfg),
    );
    if (!hasLocal) {
      setDeviceModels({ configs: providerConfigs, byKey: {} });
      return undefined;
    }
    let current = true;
    const fetchDevices = async () => {
      let devices = [];
      try {
        const res = await fetch(`${API_URL}/connect/devices`, {
          credentials: 'include',
        });
        if (res.ok) devices = (await res.json()).devices || [];
      } catch (err) {
        console.error('Failed to fetch Connect devices:', err);
      }
      if (!current) return;
      setDeviceModels({
        configs: providerConfigs,
        byKey: deviceModelsByKey(providerConfigs, devices),
      });
    };
    fetchDevices();
    return () => {
      current = false;
    };
  }, [user?.id, keysLoaded, providerConfigs]);

  // Everything a model field lists (ModelSelect).
  const modelLists = useMemo(
    () => ({
      catalog: modelCatalog,
      availableModels,
      recommended: modelRecommended,
      localModels: deviceModels.byKey,
      localModelsLoaded: deviceModels.configs === providerConfigs,
    }),
    [modelCatalog, availableModels, modelRecommended, deviceModels,
      providerConfigs],
  );

  // True when the live model selection differs from what was last loaded
  // for the active profile — i.e. there are unsaved edits a switch would
  // discard (#13).
  const isModelDirty =
    !!modelBaseline &&
    JSON.stringify({ provider, model, agentModelAssignments }) !==
      JSON.stringify(modelBaseline);

  return {
    provider,
    setProvider,
    model,
    setModel,
    agentModelAssignments,
    setAgentModelAssignments,
    providerConfigs,
    personas,
    toolApiKeys,
    requireProviderKeys,
    modelRecommended,
    modelCatalog,
    modelLists,
    isRefreshing,
    refreshModelSettings,
    profiles,
    activeProfileName,
    switchProfile,
    isModelDirty,
  };
};
